import type React from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import NewReport from '@/assets/icons/new_report.svg?component';
import Plus from '@/assets/icons/plus.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import {
    type CreateSmoltReportBody,
    DEFAULT_SMOLT_LAKSVEL_PARAMETERS,
    type SmoltLaksvelParameter,
} from '@/domain/smoltReport';
import { toDateString } from '@/utils/date';

import LaksvelParameterChecklist from './components/LaksvelParameterChecklist';

import styles from './CreateSmoltReportModal.module.scss';

interface FormDataState {
    stockingDate: Date | null;
    deliveryDate: Date | null;
    lastFeedingDate: Date | null;
    laksvelParameters: SmoltLaksvelParameter[];
}

function getTodayStart(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

interface CreateSmoltReportModalProps {
    onSubmitted: (body: CreateSmoltReportBody) => Promise<void>;
}

export function CreateSmoltReportModal(props: CreateSmoltReportModalProps): React.ReactElement {
    const { onSubmitted } = props;

    const { t } = useTranslation(['smoltReport', 'common']);
    const [open, setOpen] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const modalId = useId();
    const stockingDateId = useId();
    const deliveryDateId = useId();
    const lastFeedingDateId = useId();

    const defaultDeliveryDate = useMemo(() => getTodayStart(), []);

    const [formData, setFormData] = useState<FormDataState>({
        stockingDate: null,
        deliveryDate: defaultDeliveryDate,
        lastFeedingDate: null,
        laksvelParameters: [...DEFAULT_SMOLT_LAKSVEL_PARAMETERS],
    });

    useEffect(() => {
        if (open) {
            setFormData({
                stockingDate: null,
                deliveryDate: getTodayStart(),
                lastFeedingDate: null,
                laksvelParameters: [...DEFAULT_SMOLT_LAKSVEL_PARAMETERS],
            });
            setSubmitError(null);
        }
    }, [open]);

    function updateForm<T extends keyof FormDataState>(key: T) {
        return (value: FormDataState[T]) => {
            setFormData((prev) => ({ ...prev, [key]: value }));
            setSubmitError(null);
        };
    }

    const toggle = (): void => {
        setOpen(!open);
        setSubmitError(null);
    };

    const hasRequiredFields = !!(formData.stockingDate && formData.deliveryDate);

    const lastFeedingBeforeDelivery = ((): boolean => {
        if (!formData.lastFeedingDate || !formData.deliveryDate) return true;
        return toDateString(formData.lastFeedingDate) < toDateString(formData.deliveryDate);
    })();

    const canSubmit = hasRequiredFields;

    const onSubmit = async (ev: React.FormEvent<HTMLFormElement>): Promise<void> => {
        ev.preventDefault();
        if (!hasRequiredFields) return;

        if (!lastFeedingBeforeDelivery) {
            setSubmitError(t('smoltReport:createSmoltReport.lastFeedingBeforeDeliveryError'));
            return;
        }
        setSubmitError(null);

        const body: CreateSmoltReportBody = {
            stockingDate: toDateString(formData.stockingDate),
            deliveryDate: toDateString(formData.deliveryDate),
            ...(formData.lastFeedingDate != null && {
                lastFeedingDate: toDateString(formData.lastFeedingDate),
            }),
            laksvelParameters: formData.laksvelParameters,
        };

        setOpen(false);
        await onSubmitted(body);
    };

    return (
        <>
            <Button
                id={modalId}
                className={styles.createReportButton}
                variant="primary"
                size="medium"
                data-testid={`${modalId}-openNewSmoltReportModal`}
                trackingId="smolt_report_create_open"
                onClick={() => setOpen(true)}
                icon={<SvgIcon color="currentColor" icon={Plus} size="custom" customSize={18} />}>
                {t('smoltReport:createSmoltReport.newReport')}
            </Button>
            <ModalComponent
                className={styles.modalDialog}
                isOpen={open}
                onClose={toggle}
                keyboard={true}
                centered={true}
                title={t('smoltReport:createSmoltReport.newReport')}
                titleIcon={NewReport}
                closeTrackingId="smolt_report_create_close">
                <form onSubmit={onSubmit}>
                    <ModalBody>
                        <FormField
                            label={
                                <>
                                    {t('smoltReport:reportList.stockingDate')}{' '}
                                    <span className={styles.requiredLabel}>
                                        ({t('smoltReport:createSmoltReport.required')})
                                    </span>
                                </>
                            }
                            htmlFor={stockingDateId}>
                            <StyledSingleDatePicker
                                date={formData.stockingDate}
                                required={true}
                                onDateChange={updateForm('stockingDate')}
                            />
                        </FormField>
                        <FormField
                            label={t('smoltReport:reportList.lastFeedingDate')}
                            htmlFor={lastFeedingDateId}>
                            <StyledSingleDatePicker
                                date={formData.lastFeedingDate}
                                required={false}
                                onDateChange={updateForm('lastFeedingDate')}
                            />
                        </FormField>
                        <FormField
                            label={
                                <>
                                    {t('smoltReport:reportList.deliveryDate')}{' '}
                                    <span className={styles.requiredLabel}>
                                        ({t('smoltReport:createSmoltReport.required')})
                                    </span>
                                </>
                            }
                            htmlFor={deliveryDateId}>
                            <StyledSingleDatePicker
                                date={formData.deliveryDate}
                                required={true}
                                onDateChange={updateForm('deliveryDate')}
                            />
                        </FormField>
                        <LaksvelParameterChecklist
                            selected={formData.laksvelParameters}
                            onSelectedChange={updateForm('laksvelParameters')}
                        />
                    </ModalBody>
                    {submitError != null ? (
                        <div className={styles.formError}>
                            <Alert tone="danger" size="small">
                                {submitError}
                            </Alert>
                        </div>
                    ) : null}
                    <ModalFooter>
                        <Button
                            variant="secondary"
                            type="button"
                            trackingId="smolt_report_create_cancel"
                            onClick={() => setOpen(false)}>
                            {t('common:actions.cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            trackingId="smolt_report_create_submit"
                            disabled={!canSubmit}>
                            {t('smoltReport:createSmoltReport.createReport')}
                        </Button>
                    </ModalFooter>
                </form>
            </ModalComponent>
        </>
    );
}
